import React, { useState, useRef, useEffect } from 'react';
import { Star, X, Camera, Upload, RefreshCw } from 'lucide-react';
import Image from '../../../components/AppImage';

export default function ImageGalleryManager({
  images,
  onRemove,
  onSetPrimary,
  existingPhotos = [],
  onDeleteExisting,
  onSetExistingPrimary,
  onImagesChange
}) {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [cameraError, setCameraError] = useState('');
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const totalImages = existingPhotos.length + (images?.length || 0);
  const maxImages = 12;
  const atLimit = totalImages >= maxImages;

  // ── Camera helpers ──────────────────────────────────────────────────
  const startCamera = async (mode = facingMode) => {
    try {
      setCameraError('');
      // Stop any existing stream first
      stopStream();
      const mediaStream = await navigator.mediaDevices?.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false
      });
      streamRef.current = mediaStream;
      setIsCameraActive(true);
    } catch (err) {
      console.error('Camera access error:', err);
      setCameraError('Unable to access camera. Please allow camera permissions and try again.');
    }
  };

  // Attach stream to video element whenever both are ready
  useEffect(() => {
    if (isCameraActive && streamRef.current && videoRef.current) {
      videoRef.current.srcObject = streamRef.current;
      videoRef.current.play().catch(e => console.error('Video play error:', e));
    }
  }, [isCameraActive]);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  const stopCamera = () => {
    stopStream();
    setIsCameraActive(false);
    setCameraError('');
  };

  const switchCamera = async () => {
    const nextMode = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(nextMode);
    await startCamera(nextMode);
  };

  // Clean up stream on unmount
  useEffect(() => () => stopStream(), []);

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);

    canvas.toBlob((blob) => {
      if (!blob) return;
      // Convert blob to a proper File so uploadInventoryPhoto can handle it
      const file = new File([blob], `camera-${Date.now()}.jpg`, { type: 'image/jpeg' });
      const newImage = {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(blob),
        source: 'camera'
      };
      onImagesChange([...(images || []), newImage]);
    }, 'image/jpeg', 0.95);
  };

  // ── File upload ──────────────────────────────────────────────────────
  const handleImageAdd = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (atLimit) {
      alert(`Maximum ${maxImages} images allowed per item`);
      return;
    }

    try {
      const { compressImage } = await import('../../../utils/imageUtils');
      const compressedFile = await compressImage(file, 1920, 0.85); // Compress down to max 1920px width
      const newImage = {
        id: Math.random().toString(36).substr(2, 9),
        file: compressedFile,
        preview: URL.createObjectURL(compressedFile),
        source: 'upload'
      };
      onImagesChange([...(images || []), newImage]);
    } catch (err) {
      console.error('Failed to compress image:', err);
      const newImage = {
        id: Math.random().toString(36).substr(2, 9),
        file,
        preview: URL.createObjectURL(file),
        source: 'upload'
      };
      onImagesChange([...(images || []), newImage]);
    }

    // Reset input so the same file can be re-selected if needed
    e.target.value = '';
  };

  const handleRemoveNew = (id) => {
    onRemove ? onRemove(id) : onImagesChange(images.filter(img => img.id !== id));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Image Gallery</h3>
        <span className="text-sm text-muted-foreground">{totalImages}/{maxImages} images</span>
      </div>

      {/* Existing + new photos grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {/* Existing Photos */}
        {existingPhotos?.map((photo) => (
          <div
            key={photo?.id}
            className="relative group rounded-xl overflow-hidden border-2 border-border hover:border-primary/60 transition-all aspect-square"
          >
            <img
              src={photo?.signedUrl}
              alt="Inventory item"
              className="w-full h-full object-cover"
            />
            {photo?.isPrimary && (
              <div className="absolute top-2 left-2 bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full font-medium">
                Primary
              </div>
            )}
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center gap-2">
              {!photo?.isPrimary && (
                <button
                  onClick={() => onSetExistingPrimary?.(photo?.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity bg-primary hover:bg-primary/90 text-primary-foreground p-2 rounded-full"
                  title="Set as primary"
                  type="button"
                >
                  <Star className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={() => onDeleteExisting?.(photo?.id, photo?.photoPath)}
                className="opacity-0 group-hover:opacity-100 transition-opacity bg-destructive hover:bg-destructive/90 text-destructive-foreground p-2 rounded-full"
                title="Delete"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {/* Newly added images (not yet uploaded) */}
        {images?.map((image, index) => (
          <div
            key={image?.id}
            className="relative group rounded-xl overflow-hidden border-2 border-primary/40 transition-all aspect-square"
          >
            <img
              src={image?.preview}
              alt={`New photo ${index + 1}`}
              className="w-full h-full object-cover"
            />
            {/* Source badge */}
            <div className="absolute top-2 left-2">
              <span className="bg-black/60 text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1 backdrop-blur-sm">
                {image.source === 'camera' ? <Camera className="w-3 h-3" /> : <Upload className="w-3 h-3" />}
                {image.source === 'camera' ? 'Camera' : 'Upload'}
              </span>
            </div>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-all flex items-center justify-center">
              <button
                onClick={() => handleRemoveNew(image?.id)}
                className="opacity-0 group-hover:opacity-100 transition-opacity bg-destructive hover:bg-destructive/90 text-destructive-foreground p-2 rounded-full"
                title="Remove"
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}

        {/* Add Photo tile — only shown when under the limit and camera is not active */}
        {!atLimit && !isCameraActive && (
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-border rounded-xl hover:border-primary hover:bg-primary/5 transition-all cursor-pointer aspect-square">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageAdd}
            />
            <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center mb-2">
              <Upload className="w-5 h-5 text-primary" />
            </div>
            <span className="text-sm font-medium text-muted-foreground">Upload Photo</span>
          </label>
        )}
      </div>

      {/* ── Camera section ─────────────────────────────────────────── */}
      {!atLimit && (
        <div className="rounded-xl border border-border overflow-hidden">
          {!isCameraActive ? (
            /* Camera launch button */
            <button
              type="button"
              onClick={() => startCamera()}
              className="w-full flex items-center justify-center gap-3 py-4 px-5 bg-muted/40 hover:bg-muted/70 transition-colors"
            >
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Camera className="w-5 h-5 text-primary" />
              </div>
              <div className="text-left">
                <p className="text-sm font-semibold text-foreground">Capture with Camera</p>
                <p className="text-xs text-muted-foreground">Use your phone or laptop camera to take a photo</p>
              </div>
            </button>
          ) : (
            /* Live camera viewfinder */
            <div className="space-y-0">
              <div className="relative bg-black aspect-video">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover"
                />
                <canvas ref={canvasRef} className="hidden" />

                {/* Controls overlay */}
                <div className="absolute bottom-4 left-0 right-0 flex items-center justify-center gap-5">
                  {/* Switch camera */}
                  <button
                    type="button"
                    onClick={switchCamera}
                    className="w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white transition-colors"
                    title="Switch camera"
                  >
                    <RefreshCw className="w-5 h-5" />
                  </button>

                  {/* Shutter */}
                  <button
                    type="button"
                    onClick={capturePhoto}
                    className="w-16 h-16 rounded-full bg-white hover:bg-white/90 transition-colors border-4 border-white/50 flex items-center justify-center shadow-lg"
                    title="Take photo"
                  >
                    <Camera className="w-7 h-7 text-black" />
                  </button>

                  {/* Close camera */}
                  <button
                    type="button"
                    onClick={stopCamera}
                    className="w-11 h-11 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm border border-white/20 flex items-center justify-center text-white transition-colors"
                    title="Close camera"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Photo count badge */}
                <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full backdrop-blur-sm">
                  {totalImages}/{maxImages}
                </div>
              </div>

              {/* Tip below viewfinder */}
              <p className="text-xs text-muted-foreground text-center py-2 bg-muted/30">
                Tap the shutter button to capture · Camera stays open for multiple shots
              </p>
            </div>
          )}
        </div>
      )}

      {cameraError && (
        <p className="text-xs text-destructive flex items-center gap-1.5">
          <X className="w-3.5 h-3.5" /> {cameraError}
        </p>
      )}

      {atLimit && (
        <p className="text-xs text-muted-foreground text-center">
          Maximum {maxImages} images reached. Remove an image to add more.
        </p>
      )}
    </div>
  );
}